import { OTHER_NUMERICAL_PROPERTIES_GROUP } from "../constants";
import { jsonContentType } from "./backendRequests";
import noImage from "../static/img/microplot_placeholder.png";

//------------ Truncate Decimal to 3 digit
export const truncateNumber = (value, precision = 3) => {
  if (typeof value === "string") return value;
  const parsedValue = Number.parseFloat(value);

  if (Number.isNaN(parsedValue)) return value == null ? "" : value.toString();

  const asString = parsedValue.toString();
  // Exponential notation ("2e-7", "1e+21") has no decimal part to count, and
  // toFixed would flatten it to "0.000": keep the compact form as-is.
  if (asString.includes("e")) return asString;

  return (asString.split(".")[1]?.length ?? 0) > 3
    ? parsedValue.toFixed(precision)
    : asString;
};

//------------ Escape a value before injecting it in a Leaflet html string
// (divIcon, tooltip): plot properties come from client uploads.
const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
export const escapeHtml = (value) =>
  `${value}`.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

//------------- Convert boolean string to Int
export const boolStringToInt = (val) => {
  if (typeof val === "string") {
    if (["true", "on", "yes"].includes(val.toLowerCase())) return 1;
    if (["false", "off", "no"].includes(val.toLowerCase())) return 0;
  }
  return val;
};

/**
 * Format bytes as human-readable text.
 * https://stackoverflow.com/questions/10420352/converting-file-size-in-bytes-to-human-readable-string
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) return bytes + " B";

  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + " " + units[u];
}

/**
 * Format string dataset_X as human-readable text.
 * @param str string to convert.
 **/
export function datasetToTitleCase(str) {
  if (!str.includes("dataset_")) return str;

  return str
    .replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    })
    .replace("_", " ");
}

export const traitGroupCompare = (a, b) => {
  const getPriority = (traitGroup) => {
    if (traitGroup.deprecated) return 3; // Deprecated at the bottom
    if (traitGroup.name === OTHER_NUMERICAL_PROPERTIES_GROUP) return 2; // Obsolete just above deprecated
    if (traitGroup.name.toLowerCase().includes("heterogeneity")) return 1;
    return 0; // Other traits at the top
  };
  const priorityA = getPriority(a);
  const priorityB = getPriority(b);

  return priorityA - priorityB || a.name.localeCompare(b.name);
};

export const imageFetcher = (user) => async (url) => {
  const response = await fetch(url, {
    method: "GET",
    headers: jsonContentType(user.identity),
  });

  if (response.status === 404) return { url: noImage };
  if (response.ok) return await response.json();
};
